"use client";

import { useCallback, useContext, useLayoutEffect, useMemo, type ComponentType } from "react";
import kebabCase from "lodash/kebabCase";

import {
  AppRouterContext,
  AppRouterInstance,
} from "next/dist/shared/lib/app-router-context.shared-runtime";

type EventType =
  | "beforePush"
  | "beforeReplace"
  | "beforeRefresh"
  | "beforeForward"
  | "beforeBack"
  | "beforeHistoryChange"
  | "routeChangeStart"
  | "routeChangeComplete";

const allEvents: EventType[] = [
  "beforePush",
  "beforeReplace",
  "beforeRefresh",
  "beforeForward",
  "beforeBack",
  "beforeHistoryChange",
  "routeChangeStart",
  "routeChangeComplete",
];

/**
 * `beforePush` / `beforeReplace` are not applied via `patchRouteMethods` — they run in
 * `applyNavigationInterceptors` from patched `publicAppRouterInstance.push` / `replace` and from
 * the wrapped `next/link` default export (`onNavigate`, after user `onClick` and `defaultPrevented`).
 */
const routeMethodEvents: EventType[] = allEvents.filter(
  (ev) => ev !== "beforePush" && ev !== "beforeReplace",
);

export interface PatchedAppRouterInstance extends AppRouterInstance {
  events: EventObject;
  pathname: string;
  query: object;
  asPath: string;
}

/** Return `false` to cancel the routed method. Used for non-push/replace `router.events`. */
type Callback = (...args: unknown[]) => boolean;

/**
 * `beforePush` / `beforeReplace`: return `false` to block navigation; return a `string` to rewrite
 * the URL for subsequent listeners in the same chain and for the final dispatch; return `true`
 * or void to leave the path unchanged for this listener.
 */
export type BeforeNavigateCallback = (path: string) => boolean | string | void;

type PushReplaceEvent = "beforePush" | "beforeReplace";

type EventObject = {
  on(ev: PushReplaceEvent, callback: BeforeNavigateCallback): void;
  on(ev: Exclude<EventType, PushReplaceEvent>, callback: Callback): void;
  off(ev: PushReplaceEvent, callback: BeforeNavigateCallback): void;
  off(ev: Exclude<EventType, PushReplaceEvent>, callback: Callback): void;
  _events: Partial<Record<EventType, BeforeNavigateCallback[] | Callback[]>>;
};

const rewriteMethodName = (method: EventType): string => kebabCase(method).split("-")[1];

const publicRouterOriginalNav = Symbol.for("__stadiumPublicAppRouterOriginalPushReplace__");
const originalNextLink = Symbol.for("__stadiumOriginalNextLinkDefault__");

/** Set in `AppRouterProvider` layout effect so the patched `<Link>` can read the live bus. */
const navigationEventRef: { current: EventObject | null } = { current: null };

type NavigateType = "push" | "replace";

type NavigationInterceptBlocked = { blocked: true };
type NavigationInterceptAllowed = { blocked: false; href: string };
type NavigationInterceptResult = NavigationInterceptBlocked | NavigationInterceptAllowed;

function formatHrefPropForNavigation(href: unknown): string {
  if (typeof href === "string") return href;
  if (href && typeof href === "object") {
    try {
      const { formatUrl } = require("next/dist/shared/lib/router/utils/format-url");
      return formatUrl(href as Parameters<typeof formatUrl>[0]);
    } catch {
      const p = (href as { pathname?: string }).pathname;
      return typeof p === "string" ? p : "";
    }
  }
  return "";
}

function pathStringFromHref(href: string): string {
  try {
    const u = new URL(
      href,
      typeof window !== "undefined" ? window.location.href : "http://localhost",
    );
    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return href;
  }
}

/** Shared by patched `push`/`replace` and patched `<Link onNavigate>`. */
function applyNavigationInterceptors(
  event: EventObject,
  href: string,
  navigateType: NavigateType,
): NavigationInterceptResult {
  let pathForCallbacks = pathStringFromHref(href);
  let effectiveHref = href;

  const callbacks = (
    navigateType === "replace" ? event._events.beforeReplace : event._events.beforePush
  ) as BeforeNavigateCallback[] | undefined;

  if (callbacks?.length) {
    for (const cb of callbacks) {
      const out = cb(pathForCallbacks);
      if (out === false) {
        const blocked: NavigationInterceptBlocked = { blocked: true };
        return blocked;
      }
      if (typeof out === "string") {
        effectiveHref = out;
        pathForCallbacks = pathStringFromHref(out);
      }
    }
  }

  const allowed: NavigationInterceptAllowed = { blocked: false, href: effectiveHref };
  return allowed;
}

const patchRouteMethods = (updatedRouter, ev: EventType, appRouter: AppRouterInstance, event) => {
  const _ev = rewriteMethodName(ev);
  const originalMethod = appRouter[_ev];
  updatedRouter[_ev] = (...args) => {
    if (event._events[ev]?.length) {
      const shouldConfirm = event._events[ev].some((cb) => (cb as Callback)(...args) === false);
      if (shouldConfirm) {
        return;
      }
    }
    return originalMethod(...args);
  };
  return updatedRouter;
};

type PublicRouter = AppRouterInstance & {
  push: (href: string, options?: { scroll?: boolean }) => void;
  replace: (href: string, options?: { scroll?: boolean }) => void;
  [publicRouterOriginalNav]?: { push: PublicRouter["push"]; replace: PublicRouter["replace"] };
};

function installPublicRouterNavigateInterceptor(event: EventObject): () => void {
  try {
    const mod = require("next/dist/client/components/app-router-instance") as {
      publicAppRouterInstance: PublicRouter;
    };
    const pir = mod.publicAppRouterInstance;
    if (!pir || typeof pir.push !== "function" || typeof pir.replace !== "function") {
      return () => {};
    }

    if (!pir[publicRouterOriginalNav]) {
      pir[publicRouterOriginalNav] = {
        push: pir.push.bind(pir),
        replace: pir.replace.bind(pir),
      };
    }
    const { push: origPush, replace: origReplace } = pir[publicRouterOriginalNav];

    pir.push = (href: string, options?: { scroll?: boolean }) => {
      const result = applyNavigationInterceptors(event, href, "push");
      if (result.blocked === true) return;
      return origPush(result.href, options);
    };
    pir.replace = (href: string, options?: { scroll?: boolean }) => {
      const result = applyNavigationInterceptors(event, href, "replace");
      if (result.blocked === true) return;
      return origReplace(result.href, options);
    };

    return () => {
      const backup = pir[publicRouterOriginalNav];
      if (backup) {
        pir.push = backup.push;
        pir.replace = backup.replace;
        delete pir[publicRouterOriginalNav];
      }
    };
  } catch {
    return () => {};
  }
}

type NextLinkOnNavigateApi = { preventDefault: () => void };

type NextLinkProps = Record<string, unknown> & {
  href?: unknown;
  replace?: boolean;
  onNavigate?: (api: NextLinkOnNavigateApi) => void;
};

/**
 * Wraps Next’s default `<Link>` so `router.events` run inside Next’s `onNavigate` slot (after
 * user `onClick` and `defaultPrevented`, before `dispatchNavigateAction`).
 */
function createStadiumPatchedNextLink(
  OriginalLink: ComponentType<NextLinkProps>,
): ComponentType<NextLinkProps> {
  function StadiumPatchedNextLink(props: NextLinkProps) {
    const { onNavigate: userOnNavigate, href, replace } = props;
    const appRouter = useContext(AppRouterContext);

    const mergedOnNavigate = useCallback(
      (nav: NextLinkOnNavigateApi) => {
        const bus = navigationEventRef.current;
        if (!bus) {
          userOnNavigate?.(nav);
          return;
        }

        const hrefStr = formatHrefPropForNavigation(href);
        const navigateType: NavigateType = replace ? "replace" : "push";
        const result = applyNavigationInterceptors(bus, hrefStr, navigateType);

        if (result.blocked === true) {
          nav.preventDefault();
          return;
        }

        if (result.href !== hrefStr && appRouter) {
          nav.preventDefault();
          if (navigateType === "replace") {
            void appRouter.replace(result.href);
          } else {
            void appRouter.push(result.href);
          }
          return;
        }

        userOnNavigate?.(nav);
      },
      [userOnNavigate, href, replace, appRouter],
    );

    return <OriginalLink {...props} onNavigate={mergedOnNavigate} />;
  }

  StadiumPatchedNextLink.displayName = "StadiumPatchedNextLink";
  return StadiumPatchedNextLink;
}

function installNextLinkNavigationWrapper(): () => void {
  try {
    const linkMod = require("next/dist/client/app-dir/link") as {
      default: ComponentType<NextLinkProps>;
      [originalNextLink]?: ComponentType<NextLinkProps>;
    };

    if (!linkMod.default) return () => {};

    if (!linkMod[originalNextLink]) {
      linkMod[originalNextLink] = linkMod.default;
    }
    const Original = linkMod[originalNextLink]!;
    linkMod.default = createStadiumPatchedNextLink(Original);

    return () => {
      linkMod.default = Original;
      delete linkMod[originalNextLink];
    };
  } catch {
    return () => {};
  }
}

export function AppRouterProvider({ children }) {
  const appRouter = useContext(AppRouterContext);

  const event: EventObject = useMemo(() => {
    const events: EventObject["_events"] = {};
    const on = (ev: EventType, callback: Callback | BeforeNavigateCallback): void => {
      if (typeof callback !== "function") throw new Error("callback should be a function.");
      const list = events[ev];
      if (list) {
        list.push(callback as never);
        return;
      }
      events[ev] = [callback as never];
    };
    const off = (ev: EventType, callback: Callback | BeforeNavigateCallback): void => {
      const list = events[ev];
      if (!list) return;
      events[ev] = list.filter((cb) => cb !== callback) as never;
    };
    return {
      on,
      off,
      _events: events,
    };
  }, []);

  useLayoutEffect(() => {
    if (!appRouter) return;

    navigationEventRef.current = event;

    const patchedMethods = routeMethodEvents.reduce(
      (updatedRouter, ev) => patchRouteMethods(updatedRouter, ev, appRouter, event),
      {},
    );

    const valuePairsPatchedMethods = Object.fromEntries(
      Object.entries(patchedMethods).map(([key, value]) => [
        key,
        { value, writable: true, enumerable: true },
      ]),
    );

    Object.defineProperties(appRouter, {
      events: { value: event, writable: true, enumerable: true },
      ...valuePairsPatchedMethods,
    });

    const removePublicNavInterceptor = installPublicRouterNavigateInterceptor(event);
    const removeLinkWrapper = installNextLinkNavigationWrapper();
    return () => {
      navigationEventRef.current = null;
      removePublicNavInterceptor();
      removeLinkWrapper();
    };
  }, [appRouter, event]);

  // server side - app router is null
  if (!appRouter) return children;

  return <AppRouterContext.Provider value={appRouter}>{children}</AppRouterContext.Provider>;
}

export default AppRouterProvider;
